import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { AxiosError, isAxiosError } from 'axios';
import { type InferType } from 'yup';

import { useGlobalErrorStore } from '@/components/dialogs';
import { StepsNavigation } from '@/pages/auth/components/StepsNavigation';
import {
  StyledFormDescription,
  StyledFormProvider,
  StyledFormRoot,
  StyledFormTitle,
} from '@/pages/auth/components/styles';
import { useSendOTP } from '@/pages/auth/components/VerifyPhone/hooks/useSendOTP';
import { getExpiredTimestamp, handleAxiosErrorOnTooManyRequests } from '@/pages/auth/helpers/helpers';
import { LANDING_PAGE_LINK } from '@/shared/constants/dorim.constants';
import { useAuthMethods } from '@/shared/lib/auth';
import { SignInLinks } from '@/shared/lib/react-router';
import { useOTPStoreActions } from '@/shared/stores/otp.store';

import { INVITE_TOTAL_STEPS } from '../../constants';
import { useBan } from '../../hooks/useBan';
import { useInviteNavigation } from '../../hooks/useInviteNavigation';
import { useVerifyToken } from '../../hooks/useVerifyToken';
import { useSignUpStoreActions, useSignUpStoreUserData } from '../../store/signUp.store';
import { userSchema } from '../validation-schemas';
import { RegistrationFormFields } from './RegistrationFormFields';
import type { FormValues } from './types';

const ALREADY_IN_USE_ERROR_TYPE = 'phoneAlreadyInUse';

export const InviteRegistrationForm = () => {
  const { goToNextStep, currentStepIndex } = useInviteNavigation();

  const { setUserData } = useSignUpStoreActions();
  const storedUserData = useSignUpStoreUserData();
  const { setSignUpSendData } = useOTPStoreActions();
  const { applyBan, resetBan } = useBan();
  const { signUpSendOTP, signUpCheckIpLimits } = useAuthMethods();
  const { resetSignUpAttemptCounter, updateSignUpExpiredAt } = useOTPStoreActions();
  const { t } = useTranslation('auth-sign-up');
  const { setGlobalError } = useGlobalErrorStore();
  const { mutate: verifyToken, data: verifyTokenData } = useVerifyToken();
  const { contractor_name: contractorName = '' } = verifyTokenData ?? {};
  const inviteToken = storedUserData.inviteToken ?? '';

  const methods = useForm<InferType<typeof userSchema>>({
    mode: 'onSubmit',

    resolver: yupResolver(userSchema),
    defaultValues: {
      firstName: storedUserData.firstName,
      lastName: storedUserData.lastName,
      phoneNumber: storedUserData.phoneNumber,
      isAgree: storedUserData.isAgree,
    },
  });

  const {
    formState: { isSubmitting, isSubmitSuccessful, errors },
    resetField,
    setError,
  } = methods;

  useEffect(() => {
    if (inviteToken) {
      verifyToken(
        { invite_token: inviteToken },
        { onSuccess: data => resetField('phoneNumber', { defaultValue: data.phone }) },
      );
    }
  }, [inviteToken, verifyToken, resetField]);

  const checkIpLimits = async (lifetime: number) => {
    try {
      await signUpCheckIpLimits();
      updateSignUpExpiredAt(getExpiredTimestamp(lifetime));
      goToNextStep();
    } catch (error) {
      if (isAxiosError(error)) {
        handleAxiosErrorOnTooManyRequests({
          error,
          handler: () => applyBan(parseInt(error?.response?.headers['retry-after'])),
        });
      }
    }
  };

  const sendOTP = useSendOTP({
    sendOtpApiMethod: signUpSendOTP,
    onSuccess({ lifetime, sms_version }) {
      resetBan();
      setSignUpSendData({
        lifetime,
        expiredAt: getExpiredTimestamp(lifetime),
        sms_version,
      });
    },
  });

  const submitForm = async (formData: FormValues) => {
    try {
      const { phoneNumber } = formData;

      await sendOTP({ phone: phoneNumber });

      if (phoneNumber !== storedUserData.phoneNumber) {
        resetSignUpAttemptCounter();
      }

      setUserData({ ...formData, inviteToken });

      goToNextStep();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (isAxiosError(error)) {
          handleAxiosErrorOnTooManyRequests({
            error,
            handler: () => applyBan(parseInt(error?.response?.headers['retry-after'])),
          });
        }
        handleAxiosErrorOnTooManyRequests({
          error,
          handler: () => {
            checkIpLimits(parseInt(error?.response?.headers['retry-after']));
            return;
          },
        });

        if (error.response?.data?.message) {
          setError('phoneNumber', {
            message: error.response.data.message,
            type: ALREADY_IN_USE_ERROR_TYPE,
          });
          return;
        }
      } else {
        setGlobalError(error);
      }
    }
  };

  const onBackwardClick = () => {
    window.location.href = LANDING_PAGE_LINK;
  };

  const isFormLoading = isSubmitting || isSubmitSuccessful;
  const phoneAlreadyInUse = errors.phoneNumber?.type === ALREADY_IN_USE_ERROR_TYPE;
  return (
    <StyledFormRoot>
      <StyledFormTitle>{t('forms.registration-form.title')}</StyledFormTitle>
      <StyledFormDescription>
        {t('forms.registration-form.invite-registration-description')}{' '}
        <Typography variant="Body/medium" color="neutral.900">
          {contractorName}
        </Typography>
      </StyledFormDescription>
      <StyledFormProvider methods={methods} onSubmit={submitForm}>
        <RegistrationFormFields isPhoneDisabled={true} />

        {phoneAlreadyInUse ? (
          <Link to={SignInLinks.root}>
            <Button fullWidth variant="contained" color="secondary">
              {t('forms.registration-form.actions.sign-in')}
            </Button>
          </Link>
        ) : (
          <StepsNavigation
            onBackwardClick={onBackwardClick}
            currentStep={currentStepIndex + 1}
            totalSteps={INVITE_TOTAL_STEPS}
            backwardButtonText={t('forms.registration-form.actions.go-to-main-page')}
            forwardButtonProps={{
              type: 'submit',
              loading: isFormLoading,
            }}
          />
        )}
      </StyledFormProvider>
    </StyledFormRoot>
  );
};
